import * as S from './styles';
import { PageBanner } from 'base/src/components/PageBanner/index';
import { RootStateCarteira } from '../../store/carteiraReducer';
import { useEffect, useState } from 'react';
import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { GetExtrato } from '../../store/extrato/actions/extratoAction';
import ExtratoList from './components/ExtratoList';
import NoData from 'base/src/components/NoData';
import { Tabs, TabsContent } from 'base/src/components/Tabs';
import { TextField, Box } from '@mui/material';
import { Button } from 'base/src/components/Button';
import { Icon } from '@iconify/react';
import { useTheme } from 'base/src/utils/context/themeContext';

const connector = connectUtil(
  (state: RootStateCarteira) => ({
    ExtratoRange: state.ExtratoReducer.Extrato,
    CarteiraUsuario: state.CarteiraBaseReducer.CarteiraUsuario,
    HomeInfo: state.CarteiraBaseReducer.CarteiraBase,
  }),
  { GetExtrato }
);

export interface ExtratoComponentProps extends PropsFromRedux<typeof connector> {
}

function Extrato(props: ExtratoComponentProps) {
  const { theme } = useTheme();

  function toLocalDateString(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const getDefaultStartDate = () => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return toLocalDateString(weekAgo);
  };

  const getDefaultEndDate = () => {
    return toLocalDateString(new Date());
  };

  const [dateStart, setDateStart] = useState(getDefaultStartDate());
  const [dateEnd, setDateEnd] = useState(getDefaultEndDate());
  const [selectedTab, setSelectedTab] = useState('semana');

  function calculateDateRange(range: string) {
    const today = new Date();
    const endDate = new Date();
    let startDate = new Date();

    switch (range) {
      case 'semana':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'mes':
        startDate.setMonth(today.getMonth() - 1);
        break;
      case 'ano':
        startDate.setFullYear(today.getFullYear() - 1);
        break;
      default:
        startDate.setDate(today.getDate() - 7);
    }

    return {
      start: toLocalDateString(startDate),
      end: toLocalDateString(endDate)
    };
  }

  function handleTabChange(value: string) {
    setSelectedTab(value);
    const range = calculateDateRange(value);
    setDateStart(range.start);
    setDateEnd(range.end);
    fetchExtratoWithDates(range.start, range.end);
  }

  function fetchExtratoWithDates(start: string, end: string) {
    if (!props.CarteiraUsuario?.length || props.CarteiraUsuario[0].CarteiraID === 0) return;

    const request = {
      From: start ? new Date(start + 'T00:00:00').toDateString() : '',
      To: end ? new Date(end + 'T00:00:00').toDateString() : '',
    };

    props.GetExtrato(request, props.CarteiraUsuario[0].CarteiraID, () => { });
  }

  function handleApplyDateFilter() {
    fetchExtratoWithDates(dateStart, dateEnd);
  }

  useEffect(function fetchInitialExtrato() {
    fetchExtratoWithDates(dateStart, dateEnd);
  }, [props.CarteiraUsuario])

  return (
    <S.Container>
      <PageBanner title='Extrato' subtile='Histórico de pagamento(s) e depósito(s)' />

      <S.FilterSection>
        <Tabs
          defaultValue="semana"
          onValueChange={handleTabChange}
          items={[
            { value: 'semana', label: 'Semana' },
            { value: 'mes', label: 'Mês' },
            { value: 'ano', label: 'Ano' }
          ]}
        >
          <TabsContent value={selectedTab}>
            <S.DateFilterContainer>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <TextField
                  label="Data Inicial"
                  type="date"
                  size="small"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{
                    minWidth: '150px',
                    flex: 1,
                    maxWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.colors.card,
                      '&:hover fieldset': {
                        borderColor: theme.colors.primary,
                      },
                    },
                  }}
                />
                <TextField
                  label="Data Final"
                  type="date"
                  size="small"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{
                    minWidth: '150px',
                    flex: 1,
                    maxWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.colors.card,
                      '&:hover fieldset': {
                        borderColor: theme.colors.primary,
                      },
                    },
                  }}
                />
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handleApplyDateFilter}
                >
                  <Icon icon="ph:funnel-bold" height={16} />
                  Filtrar
                </Button>
              </Box>
            </S.DateFilterContainer>
          </TabsContent>
        </Tabs>
      </S.FilterSection>

      {
        props.ExtratoRange === undefined || props.ExtratoRange.length < 1 ? (
          <NoData />
        ) :
          (
            <ExtratoList extrato={props.ExtratoRange} hideValues={props.HomeInfo.EsconderSaldo} />
          )
      }

    </S.Container>
  )
}

export default connector(Extrato);
